class StatsSection extends HTMLElement {
  constructor() {
    super();
    this.observers = [];
    this.animatedNumbers = new Set();
    this.init();
  }

  init() {
    this.setupIntersectionObserver();
  }

  setupIntersectionObserver() {
    const options = {
      threshold: 0.2,
      rootMargin: '0px 0px -50px 0px'
    };

    this.observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          this.animateNumbers();
        }
      });
    }, options);

    this.observer.observe(this);
  }

  animateNumbers() {
    const numbers = this.querySelectorAll('.stats__number[data-target]');
    
    numbers.forEach(numberElement => {
      // Skip if already animated
      if (this.animatedNumbers.has(numberElement)) return;
      
      this.animatedNumbers.add(numberElement);
      this.animateNumber(numberElement);
    });

    // Stop observing after animation starts
    this.observer.disconnect();
  }

  animateNumber(element) {
    const target = parseFloat(element.dataset.target) || 0;
    const duration = parseInt(element.dataset.duration) || 2000;
    const decimalPlaces = parseInt(element.dataset.decimalPlaces) || 0;
    const prefix = element.dataset.prefix || '';
    const suffix = element.dataset.suffix || '';

    // Mark as animating
    element.setAttribute('data-animating', 'true');
    element.setAttribute('data-loaded', 'false');

    let startTime = null;
    const startValue = 0;

    const animate = (currentTime) => {
      if (!startTime) startTime = currentTime;
      
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);

      // Easing function (ease-out)
      const easeOut = 1 - Math.pow(1 - progress, 3);
      const currentValue = startValue + (target - startValue) * easeOut;

      // Format the number
      let formattedNumber;
      if (decimalPlaces > 0) {
        formattedNumber = currentValue.toFixed(decimalPlaces);
      } else {
        formattedNumber = Math.floor(currentValue).toString();
      }

      // Update the display
      element.textContent = `${prefix}${formattedNumber}${suffix ? suffix : ''}`;

      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        // Animation complete
        element.setAttribute('data-animating', 'false');
        element.setAttribute('data-loaded', 'true');
        element.parentElement.parentElement.setAttribute('data-animated', 'true');
        
        // Final value to ensure precision
        const finalValue = decimalPlaces > 0 ? target.toFixed(decimalPlaces) : target.toString();
        element.textContent = `${prefix}${finalValue}${suffix ? suffix : ''}`;
      }
    };

    requestAnimationFrame(animate);
  }

  // Helper method to manually trigger animation (for debugging)
  triggerAnimation() {
    this.animateNumbers();
  }

  // Cleanup
  disconnectedCallback() {
    if (this.observer) {
      this.observer.disconnect();
    }
    this.observers.forEach(observer => observer.disconnect());
    this.observers = [];
    this.animatedNumbers.clear();
  }
}

// Error handling for counter animation
class SafeStatsSection extends StatsSection {
  animateNumber(element) {
    try {
      super.animateNumber(element);
    } catch (error) {
      console.warn('Stats animation error:', error);
      
      // Fallback: just show the final value
      const target = element.dataset.target || '0';
      const prefix = element.dataset.prefix || '';
      const suffix = element.dataset.suffix || '';
      const decimalPlaces = parseInt(element.dataset.decimalPlaces) || 0;
      
      const finalValue = decimalPlaces > 0 ? 
        parseFloat(target).toFixed(decimalPlaces) : 
        target;
        
      element.textContent = `${prefix}${finalValue}${suffix ? suffix : ''}`;
      element.setAttribute('data-loaded', 'true');
    }
  }
}

// Register the component
if (!customElements.get('stats-section')) {
  customElements.define('stats-section', SafeStatsSection);
}

// Initialize any existing stats sections on page load
document.addEventListener('DOMContentLoaded', () => {
  const existingSections = document.querySelectorAll('stats-section');
  existingSections.forEach(section => {
    if (!section.initialized) {
      section.initialized = true;
    }
  });
});

// Export for potential manual usage
window.StatsSection = SafeStatsSection;